import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, BellRing, Users, FileText, Wallet } from "lucide-react";
import { StatusBadge, brl, fdate } from "@/pages/portal/Dashboard";

const STATUS_ORDER = ["Rascunho", "Em análise", "Em orçamento", "Orçamento enviado", "Aguardando aprovação",
                      "Aprovado", "Em produção", "Concluído", "Cancelado"];

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (!data) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }

  const totals = [
    { icon: FileText, label: "Orçamentos", value: data.total_quotes, testId: "admin-stat-quotes" },
    { icon: Users, label: "Clientes", value: data.total_customers, testId: "admin-stat-customers" },
    { icon: Wallet, label: "Valor total", value: brl(data.total_value), testId: "admin-stat-value" },
    { icon: BellRing, label: "Notificações pendentes", value: data.pending_notifications, testId: "admin-stat-pending" },
  ];

  return (
    <div data-testid="admin-dashboard-page">
      <h1 className="display text-3xl font-extrabold uppercase">Dashboard</h1>
      <p className="mt-1 text-sm text-slate-500">Visão geral da operação Alpha Metais.</p>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {totals.map((t) => (
          <div key={t.label} className="card-tactical rounded-md p-5" data-testid={t.testId}>
            <t.icon className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
            <div className="display mt-3 text-2xl sm:text-3xl font-extrabold">{t.value}</div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{t.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 card-tactical rounded-md p-6" data-testid="admin-status-board">
        <h2 className="display text-lg font-bold uppercase tracking-wide">Orçamentos por status</h2>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {STATUS_ORDER.map((s) => (
            <Link key={s} to="/solicitacoes" data-testid={`admin-status-${s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, "-")}`}
              className="rounded-md border border-slate-800 bg-slate-900/40 p-4 hover:border-orange-900/60 transition-colors">
              <div className="display text-2xl font-extrabold">{data.by_status?.[s] || 0}</div>
              <div className="mt-1"><StatusBadge status={s} /></div>
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-5">
        <section className="card-tactical rounded-md p-6" data-testid="admin-recent-quotes">
          <h2 className="display text-lg font-bold uppercase tracking-wide">Orçamentos recentes</h2>
          <ul className="mt-4 space-y-3">
            {data.recent_quotes.map((q) => (
              <li key={q.id}>
                <Link to={`/solicitacoes/${q.id}`} className="flex items-center justify-between gap-3 group">
                  <div className="min-w-0">
                    <div className="mono text-sm text-slate-200 group-hover:text-orange-400 transition-colors">{q.number}</div>
                    <div className="text-xs text-slate-500 truncate">{q.client_name}</div>
                  </div>
                  <StatusBadge status={q.status} />
                </Link>
              </li>
            ))}
            {!data.recent_quotes.length && <li className="text-sm text-slate-500">Nenhum orçamento ainda.</li>}
          </ul>
        </section>

        <section className="card-tactical rounded-md p-6" data-testid="admin-recent-customers">
          <h2 className="display text-lg font-bold uppercase tracking-wide">Clientes recentes</h2>
          <ul className="mt-4 space-y-3">
            {data.recent_customers.map((c) => (
              <li key={c.id}>
                <Link to={`/clientes-admin/${c.id}`} className="flex items-center justify-between gap-3 group">
                  <div className="min-w-0">
                    <div className="text-sm text-slate-200 group-hover:text-orange-400 transition-colors truncate">{c.name}</div>
                    <div className="mono text-xs text-slate-500">{c.document_fmt}</div>
                  </div>
                  <span className="text-xs text-slate-600 whitespace-nowrap">{fdate(c.created_at)}</span>
                </Link>
              </li>
            ))}
            {!data.recent_customers.length && <li className="text-sm text-slate-500">Nenhum cliente ainda.</li>}
          </ul>
        </section>

        <section className="card-tactical rounded-md p-6" data-testid="admin-recent-activity">
          <h2 className="display text-lg font-bold uppercase tracking-wide">Atividades recentes</h2>
          <ul className="mt-4 space-y-3">
            {data.recent_notifications.map((n) => (
              <li key={n.id} className="text-sm">
                <div className="text-slate-300">
                  <span className="mono text-orange-400">{n.quote_number}</span>{" "}
                  {n.new_status ? `→ ${n.new_status}` : n.type}
                </div>
                <div className="text-xs text-slate-600">{n.delivery} · {fdate(n.created_at)}</div>
              </li>
            ))}
            {!data.recent_notifications.length && <li className="text-sm text-slate-500">Nenhuma atividade ainda.</li>}
          </ul>
        </section>
      </div>
    </div>
  );
}
